import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquareText, Users2, Contact2, Loader2, Plus, Check, Briefcase, Home, X } from "lucide-react";
import { messagingApi, setMessengerSpace } from "@/api/messaging";
import { socialApi } from "@/api/social";
import P2PInbox from "@/components/chat/P2PInbox";

const CYAN = "#00D2FF";
const VIOLET = "#9B5DE5";

/* ---- swap the PWA manifest + theme-color so THIS route installs as the
   separate "Misteck Messenger" app (cyan), not the gold Misteck app ---- */
function useMessengerManifest() {
  useEffect(() => {
    const head = document.head;
    const linkEl = head.querySelector('link[rel="manifest"]');
    const prevManifest = linkEl?.getAttribute("href");
    const themeEl = head.querySelector('meta[name="theme-color"]');
    const prevTheme = themeEl?.getAttribute("content");
    if (linkEl) linkEl.setAttribute("href", "/manifest.messenger.json");
    if (themeEl) themeEl.setAttribute("content", CYAN);
    return () => {
      if (linkEl && prevManifest) linkEl.setAttribute("href", prevManifest);
      if (themeEl && prevTheme) themeEl.setAttribute("content", prevTheme);
    };
  }, []);
}

function ContactsView({ space, onOpenChat }) {
  const [friends, setFriends] = useState(null);
  const [contacts, setContacts] = useState([]);
  const load = () => {
    Promise.all([
      socialApi.friends().then((r) => r.friends || []).catch(() => []),
      messagingApi.contacts().then((r) => r.contacts || []).catch(() => []),
    ]).then(([fr, ct]) => { setFriends(fr); setContacts(ct); });
  };
  useEffect(() => { load(); }, [space]);
  const inBook = (id) => contacts.some((c) => c.id === id);
  const toggle = async (f) => {
    try {
      if (inBook(f.id)) { await messagingApi.removeContact(f.id); toast(`${f.name} removed from ${space}`); }
      else { await messagingApi.addContact(f.id); toast.success(`${f.name} added to ${space}`); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update contact"); }
  };
  if (friends == null) return <div className="grid place-items-center h-full"><Loader2 className="animate-spin text-white/40" /></div>;
  return (
    <div className="h-full overflow-y-auto no-scrollbar px-4 py-4 space-y-2" data-testid="msgr-contacts">
      <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-white/40 pb-1">{space} contacts · tap name to chat</p>
      {friends.length === 0 ? (
        <p className="font-mono text-[11px] text-white/50 py-8 text-center">No mutual friends yet. Add friends in the directory to build your {space} circle.</p>
      ) : friends.map((f) => (
        <div key={f.id} data-testid={`msgr-contact-${f.id}`} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] px-3 py-2.5">
          <button onClick={() => onOpenChat(f)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
            <span className="w-10 h-10 rounded-full grid place-items-center text-white font-mono text-[11px] font-black shrink-0" style={{ background: `linear-gradient(135deg, ${CYAN}, ${VIOLET})` }}>{(f.name || "?").slice(0, 2).toUpperCase()}</span>
            <span className="font-mono text-[12px] font-bold text-white truncate">{f.name}</span>
          </button>
          <button onClick={() => toggle(f)} data-testid={`msgr-contact-toggle-${f.id}`}
            className={`shrink-0 w-9 h-9 rounded-full grid place-items-center border ${inBook(f.id) ? "border-white/20 bg-white/10 text-white" : "border-transparent text-black"}`}
            style={inBook(f.id) ? {} : { background: CYAN }}>
            {inBook(f.id) ? <Check size={16} /> : <Plus size={16} />}
          </button>
        </div>
      ))}
    </div>
  );
}

export default function MessengerApp() {
  useMessengerManifest();
  const location = useLocation();
  const [space, setSpace] = useState("private");
  const [tab, setTab] = useState("chats"); // chats | groups | contacts
  const [chatKey, setChatKey] = useState(0);
  const deepDone = useRef(false);

  const applySpace = (s) => { setMessengerSpace(s); setSpace(s); setChatKey((k) => k + 1); };
  useEffect(() => { setMessengerSpace("private"); }, []);

  // Deep-link from a profile: /messenger?dm=<userId>&space=private
  useEffect(() => {
    if (deepDone.current) return;
    const q = new URLSearchParams(location.search);
    const dm = q.get("dm");
    const sQ = q.get("space") === "work" ? "work" : "private";
    deepDone.current = true;
    applySpace(sQ);
    if (dm) {
      messagingApi.openThread(dm)
        .then((r) => {
          sessionStorage.setItem("mist_pending_dm", JSON.stringify({ threadId: r.thread_id, other: r.other }));
          setTab("chats"); setChatKey((k) => k + 1);
        })
        .catch((e) => toast.error(e?.response?.data?.detail || "Could not open that chat"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openChatWith = async (person) => {
    try {
      const r = await messagingApi.openThread(person.id);
      sessionStorage.setItem("mist_pending_dm", JSON.stringify({ threadId: r.thread_id, other: r.other }));
      setTab("chats"); setChatKey((k) => k + 1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open chat"); }
  };

  const NAV = [
    { k: "chats", label: "Chats", Icon: MessageSquareText },
    { k: "contacts", label: "Contacts", Icon: Contact2 },
  ];

  return (
    <div className="fixed inset-0 flex flex-col" style={{ background: "#0E0E12" }} data-testid="messenger-app">
      {/* Header + space toggle */}
      <header className="shrink-0 px-4 pt-4 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: `linear-gradient(135deg, ${CYAN}, ${VIOLET})` }}>
            <MessageSquareText size={18} className="text-white" strokeWidth={2.4} />
          </span>
          <div className="leading-none flex-1">
            <p className="font-mono text-[8px] uppercase tracking-[0.3em]" style={{ color: CYAN }}>Misteck</p>
            <h1 className="font-serif text-xl text-white">Messenger</h1>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-1 rounded-full border border-white/10 bg-white/[0.04] p-1" data-testid="msgr-space-toggle">
          {[["private", "Private", Home], ["work", "Work", Briefcase]].map(([v, lbl, Icon]) => (
            <button key={v} onClick={() => applySpace(v)} data-testid={`msgr-space-${v}`}
              className={`flex items-center justify-center gap-1.5 rounded-full py-2 font-mono text-[10px] font-black uppercase tracking-widest transition-colors ${space === v ? "text-black" : "text-white/60"}`}
              style={space === v ? { background: v === "work" ? VIOLET : CYAN } : {}}>
              <Icon size={13} /> {lbl}
            </button>
          ))}
        </div>
      </header>

      {/* Body */}
      <div className="relative flex-1 min-h-0">
        {tab === "chats" && (
          <div className="absolute inset-0" data-testid="msgr-chats"><P2PInbox key={`c-${space}-${chatKey}`} embedded enableGroups onBack={() => {}} /></div>
        )}
        {tab === "contacts" && <ContactsView space={space} onOpenChat={openChatWith} />}
      </div>

      {/* Bottom nav */}
      <nav className="shrink-0 grid grid-cols-2 border-t border-white/10 bg-black/40" data-testid="msgr-nav">
        {NAV.map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`msgr-tab-${k}`}
            className="flex flex-col items-center gap-1 py-2.5"
            style={{ color: tab === k ? CYAN : "rgba(255,255,255,0.5)" }}>
            <Icon size={20} strokeWidth={tab === k ? 2.6 : 2} />
            <span className="font-mono text-[8px] uppercase tracking-widest">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
